/* Copyright (c) 2020-present CloudIO Inc */

import { Item, Page } from 'componentregistry';
import { Platform } from 'componentregistry/platform/Platform';
import { autoPopulateChildren } from 'designer/state';
import Store from 'store/Store';
import { camelCase } from 'lodash-es';
import { GeneratePage } from './ds_registry';
import {
  IoDatasourceAttributes,
  isNumberType,
} from '@cloudio-saas/datasource-types';

function getPage(ds: string, attrs: IoDatasourceAttributes[]): Page {
  const alias = ds ? camelCase(`${ds}Alias`) : '';
  const createdByAttr = attrs.find((a) => a.attributeCode === 'createdBy');
  if (!createdByAttr) {
    throw Error(`Missing createdBy attribute in dataSource ${ds}!`);
  }
  const numberAttr = isNumberType(createdByAttr.attributeType);
  const idAttr =
    attrs.find((a) => a.attributeCode === 'id') ??
    attrs.find((a) => a.primaryKey === 'Y');
  if (!idAttr) {
    throw Error(`Missing primary key attribute in dataSource ${ds}!`);
  }
  const page: {
    form: Item<'Form'>;
    icon: Item<'Icon'>;
    root: Item<'Page'>;
    icon1: Item<'Icon'>;
    paper: Item<'Paper'>;
    button: Item<'Button'>;
    boxList: Item<'BoxList'>;
    button1: Item<'Button'>;
    chartjs: Item<'Chartjs'>;
    chartjs1: Item<'Chartjs'>;
    canvasGrid: Item<'CanvasGrid'>;
    flexItem: Item<'FlexItem'>;
    boxParent: Item<'Box'>;
    tabPanel: Item<'TabPanel'>;
    dataStore: Item<'DataStore'>;
    flexItem1: Item<'FlexItem'>;
    flexItem2: Item<'FlexItem'>;
    flexItem3: Item<'FlexItem'>;
    flexItem4: Item<'FlexItem'>;
    flexItem5: Item<'FlexItem'>;
    flexItem6: Item<'FlexItem'>;
    flexItem7: Item<'FlexItem'>;
    flexItem8: Item<'FlexItem'>;
    flexPanel: Item<'FlexPanel'>;
    metricBox: Item<'MetricBox'>;
    rowAction: Item<'RowAction'>;
    actionSave: Item<'ActionSave'>;
    dataStore1: Item<'ReadOnlyDataStore'>;
    dataStore2: Item<'ReadOnlyDataStore'>;
    dataStore3: Item<'ReadOnlyDataStore'>;
    dataStore4: Item<'ReadOnlyDataStore'>;
    flexPanel1: Item<'FlexPanel'>;
    flexPanel2: Item<'FlexPanel'>;
    flexPanel3: Item<'FlexPanel'>;
    iconButton: Item<'IconButton'>;
    rowAction1: Item<'RowAction'>;
    actionQuery: Item<'ActionQuery'>;
    actionReset: Item<'ActionReset'>;
    actionSave1: Item<'ActionSave'>;
    box: Item<'Box'>;
    editTabItem: Item<'TabItem'>;
    iconButton1: Item<'IconButton'>;
    iconButton2: Item<'IconButton'>;
    iconButton3: Item<'IconButton'>;
    listTabItem: Item<'TabItem'>;
    smartSearch: Item<'SmartSearch'>;
    actionCreate: Item<'ActionCreate'>;
    actionQuery1: Item<'ActionQuery'>;
    actionReset1: Item<'ActionReset'>;
    box1: Item<'Box'>;
    smartSearch1: Item<'SmartSearch'>;
    actionCreate1: Item<'ActionCreate'>;
    triggerOnClick: Item<'TriggerOnClick'>;
    detailsCanvasGrid: Item<'CanvasGrid'>;
    rowNumberColumn: Item<'RowNumberColumn'>;
    triggerOnClick1: Item<'TriggerOnClick'>;
    triggerOnClick2: Item<'TriggerOnClick'>;
    triggerOnClick3: Item<'TriggerOnClick'>;
    triggerOnClick4: Item<'TriggerOnClick'>;
    triggerOnClick5: Item<'TriggerOnClick'>;
    triggerOnClick6: Item<'TriggerOnClick'>;
    triggerOnClick7: Item<'TriggerOnClick'>;
    triggerOnClick8: Item<'TriggerOnClick'>;
    triggerOnClick9: Item<'TriggerOnClick'>;
    actionUpdateProp: Item<'ActionUpdateProp'>;
    dashboardTabItem: Item<'TabItem'>;
    triggerOnClick10: Item<'TriggerOnClick'>;
    triggerOnClick11: Item<'TriggerOnClick'>;
    actionIconsColumn: Item<'ActionIconsColumn'>;
    actionUpdateProp1: Item<'ActionUpdateProp'>;
    actionUpdateProp2: Item<'ActionUpdateProp'>;
    actionUpdateProp3: Item<'ActionUpdateProp'>;
    dataStoreReference: Item<'DataStoreReference'>;
    metricColorAndIcon: Item<'MetricColorAndIcon'>;
    rowSelectionColumn: Item<'RowSelectionColumn'>;
    triggerBeforeQuery: Item<'TriggerBeforeQuery'>;
    dataStoreReference1: Item<'DataStoreReference'>;
    dataStoreReference2: Item<'DataStoreReference'>;
    dataStoreReference3: Item<'DataStoreReference'>;
    dataStoreReference4: Item<'DataStoreReference'>;
    metricColorAndIcon1: Item<'MetricColorAndIcon'>;
    metricColorAndIcon2: Item<'MetricColorAndIcon'>;
    metricColorAndIcon3: Item<'MetricColorAndIcon'>;
    actionSetQueryFilter: Item<'ActionSetQueryFilter'>;
    actionSetQueryFilter1: Item<'ActionSetQueryFilter'>;
    actionSetQueryFilter2: Item<'ActionSetQueryFilter'>;
    triggerOnRowClick: Item<'TriggerOnRowClick'>;
    actionDeleteCurrentRecord: Item<'ActionDeleteCurrentRecord'>;
    actionSetGroupByAttribute: Item<'ActionSetGroupByAttribute'>;
    actionDeleteCurrentRecord1: Item<'ActionDeleteCurrentRecord'>;
    actionDeleteSelectedRecords: Item<'ActionDeleteSelectedRecords'>;
    actionSetAggregateAttribute: Item<'ActionSetAggregateAttribute'>;
    folder: Item<'Folder'>;
  } = {
    folder: {
      children: [
        'dataStore',
        'dataStore3',
        'dataStore1',
        'dataStore2',
        'dataStore4',
      ],
      itemId: 'folder',
      parent: 'root',
      props: {
        icon: {
          iconName: 'database',
        },
        name: 'Data Stores',
      },
      type: 'Folder',
    },
    actionCreate: {
      children: [],
      itemId: 'actionCreate',
      parent: 'triggerOnClick',
      props: {
        alias,
      },
      type: 'ActionCreate',
    },
    actionCreate1: {
      children: [],
      itemId: 'actionCreate1',
      parent: 'triggerOnClick11',
      props: {
        alias,
      },
      type: 'ActionCreate',
    },
    actionDeleteCurrentRecord: {
      children: [],
      itemId: 'actionDeleteCurrentRecord',
      parent: 'triggerOnClick3',
      props: {
        alias,
        confirmationMessage: {
          type: 'text',
          value: 'Are you sure?',
        },
      },
      type: 'ActionDeleteCurrentRecord',
    },
    actionDeleteCurrentRecord1: {
      children: [],
      itemId: 'actionDeleteCurrentRecord1',
      parent: 'triggerOnClick5',
      props: {
        alias,
        confirmationMessage: {
          type: 'text',
          value: 'Are you sure?',
        },
      },
      type: 'ActionDeleteCurrentRecord',
    },
    actionDeleteSelectedRecords: {
      children: [],
      itemId: 'actionDeleteSelectedRecords',
      parent: 'triggerOnClick4',
      props: {
        alias,
        confirmationMessage: {
          type: 'text',
          value: 'Confirm Delete?',
        },
      },
      type: 'ActionDeleteSelectedRecords',
    },
    actionIconsColumn: {
      children: ['icon', 'icon1'],
      itemId: 'actionIconsColumn',
      parent: 'canvasGrid',
      props: {
        alignment: 'center',
        width: 99,
      },
      type: 'ActionIconsColumn',
    },
    actionQuery: {
      children: ['actionSetQueryFilter2'],
      itemId: 'actionQuery',
      parent: 'triggerOnClick9',
      props: {
        alias: 'details',
        applyFiltersToSmartSearch: true,
      },
      type: 'ActionQuery',
    },
    actionQuery1: {
      children: ['actionSetQueryFilter1'],
      itemId: 'actionQuery1',
      parent: 'triggerOnClick10',
      props: {
        alias: 'details',
        applyFiltersToSmartSearch: true,
      },
      type: 'ActionQuery',
    },
    actionReset: {
      children: [],
      itemId: 'actionReset',
      parent: 'triggerOnClick1',
      props: {
        alias,
      },
      type: 'ActionReset',
    },
    actionReset1: {
      children: [],
      itemId: 'actionReset1',
      parent: 'triggerOnClick7',
      props: {
        alias,
      },
      type: 'ActionReset',
    },
    actionSave: {
      children: [],
      itemId: 'actionSave',
      parent: 'triggerOnClick2',
      props: {
        alias,
      },
      type: 'ActionSave',
    },
    actionSave1: {
      children: [],
      itemId: 'actionSave1',
      parent: 'triggerOnClick8',
      props: {
        alias,
      },
      type: 'ActionSave',
    },
    actionSetAggregateAttribute: {
      children: [],
      itemId: 'actionSetAggregateAttribute',
      parent: 'triggerBeforeQuery',
      props: {
        attributeCode: idAttr.attributeCode,
        intoAttributeCode: idAttr.attributeCode,
      },
      type: 'ActionSetAggregateAttribute',
    },
    actionSetGroupByAttribute: {
      children: [],
      itemId: 'actionSetGroupByAttribute',
      parent: 'triggerBeforeQuery',
      props: {
        attributeCode: 'createdBy',
      },
      type: 'ActionSetGroupByAttribute',
    },
    actionSetQueryFilter: {
      children: [],
      itemId: 'actionSetQueryFilter',
      parent: 'triggerBeforeQuery',
      props: {
        attribute: 'createdBy',
        operator: numberAttr ? 'notNull' : 'notEmpty',
      },
      type: 'ActionSetQueryFilter',
    },
    actionSetQueryFilter1: {
      children: [],
      itemId: 'actionSetQueryFilter1',
      parent: 'actionQuery1',
      props: {
        attribute: 'createdBy',
        operator: numberAttr ? 'eq' : 'is',
        value: [
          {
            type: 'expression',
            value: '$.summary.createdBy',
          },
        ],
      },
      type: 'ActionSetQueryFilter',
    },
    actionSetQueryFilter2: {
      children: [],
      itemId: 'actionSetQueryFilter2',
      parent: 'actionQuery',
      props: {
        attribute: 'createdBy',
        operator: numberAttr ? 'eq' : 'is',
        value: [
          {
            type: 'expression',
            value: '$.chart1.createdBy',
          },
        ],
      },
      type: 'ActionSetQueryFilter',
    },
    actionUpdateProp: {
      children: [],
      itemId: 'actionUpdateProp',
      parent: 'triggerOnClick',
      props: {
        property: 'activeTab',
        propertyValue: 'editTabItem',
        targetItemId: 'tabPanel',
      },
      type: 'ActionUpdateProp',
    },
    actionUpdateProp1: {
      children: [],
      itemId: 'actionUpdateProp1',
      parent: 'triggerOnClick6',
      props: {
        property: 'activeTab',
        propertyValue: 'editTabItem',
        targetItemId: 'tabPanel',
      },
      type: 'ActionUpdateProp',
    },
    actionUpdateProp2: {
      children: [],
      itemId: 'actionUpdateProp2',
      parent: 'triggerOnClick11',
      props: {
        property: 'activeTab',
        propertyValue: 'editTabItem',
        targetItemId: 'tabPanel',
      },
      type: 'ActionUpdateProp',
    },
    actionUpdateProp3: {
      children: [],
      itemId: 'actionUpdateProp3',
      parent: 'triggerOnRowClick',
      props: {
        property: 'activeTab',
        propertyValue: 'editTabItem',
        targetItemId: 'tabPanel',
      },
      type: 'ActionUpdateProp',
    },
    boxList: {
      children: ['metricBox'],
      itemId: 'boxList',
      parent: 'dataStoreReference2',
      props: {
        gap: '16px',
        padding: {
          all: 16,
        },
      },
      type: 'BoxList',
    },
    button: {
      children: ['triggerOnClick2'],
      itemId: 'button',
      parent: 'box1',
      props: {
        startIcon: {
          iconName: 'save',
          iconType: 'light',
        },
        text: {
          type: 'text',
          value: 'Save',
        },
      },
      type: 'Button',
    },
    button1: {
      children: ['triggerOnClick8'],
      itemId: 'button1',
      parent: 'editTabItem',
      props: {
        startIcon: {
          iconName: 'save',
          iconType: 'light',
        },
        text: {
          type: 'text',
          value: 'Save',
        },
      },
      type: 'Button',
    },
    chartjs: {
      children: ['triggerOnClick9'],
      itemId: 'chartjs',
      parent: 'flexItem8',
      props: {
        dsalias: 'chart1',
        gridLines: 'none',
        labelAttribute: 'createdBy',
        valueAttribute: idAttr.attributeCode,
      },
      type: 'Chartjs',
    },
    chartjs1: {
      children: [],
      itemId: 'chartjs1',
      parent: 'flexItem3',
      props: {
        chartType: 'doughnut',
        dsalias: 'chart2',
        functionType: 'Count',
        labelAttribute: 'createdBy',
        valueAttribute: idAttr.attributeCode,
      },
      type: 'Chartjs',
    },
    box: {
      children: ['smartSearch'],
      itemId: 'box',
      parent: 'boxParent',
      props: {
        flexGrow: 1,
      },
      type: 'Box',
    },
    box1: {
      children: ['iconButton', 'iconButton1', 'button'],
      itemId: 'box1',
      parent: 'boxParent',
      props: {
        boxHeight: '1',
        gap: '8px',
      },
      type: 'Box',
    },
    dashboardTabItem: {
      children: ['flexPanel3', 'iconButton3'],
      itemId: 'dashboardTabItem',
      parent: 'tabPanel',
      props: {
        icon: {
          iconName: 'chart-line',
          iconType: 'light',
        },
        label: {
          type: 'text',
          value: 'Dashboard',
        },
      },
      type: 'TabItem',
    },
    canvasGrid: {
      children: [
        'rowNumberColumn',
        'rowSelectionColumn',
        'actionIconsColumn',
        'triggerOnRowClick',
      ],
      itemId: 'canvasGrid',
      parent: 'flexItem1',
      props: {},
      type: 'CanvasGrid',
    },
    dataStore: {
      children: [],
      itemId: 'dataStore',
      parent: 'folder',
      props: {
        alias,
        autoQuery: true,
        dataSource: ds,
        maxRows: 100,
      },
      type: 'DataStore',
    },
    dataStore1: {
      children: ['triggerBeforeQuery'],
      itemId: 'dataStore1',
      parent: 'folder',
      props: {
        alias: 'summary',
        autoQuery: true,
        dataSource: ds,
        maxRows: 6,
      },
      type: 'ReadOnlyDataStore',
    },
    dataStore2: {
      children: [],
      itemId: 'dataStore2',
      parent: 'folder',
      props: {
        alias: 'chart1',
        autoQuery: false,
        dataSource: ds,
      },
      type: 'ReadOnlyDataStore',
    },
    dataStore3: {
      children: [],
      itemId: 'dataStore3',
      parent: 'folder',
      props: {
        alias: 'details',
        autoQuery: true,
        dataSource: ds,
        maxRows: 100,
      },
      type: 'ReadOnlyDataStore',
    },
    dataStore4: {
      children: [],
      itemId: 'dataStore4',
      parent: 'folder',
      props: {
        alias: 'chart2',
        autoQuery: false,
        dataSource: ds,
      },
      type: 'ReadOnlyDataStore',
    },
    dataStoreReference: {
      children: ['smartSearch1'],
      itemId: 'dataStoreReference',
      parent: 'paper',
      props: {
        alias: 'details',
      },
      type: 'DataStoreReference',
    },
    dataStoreReference1: {
      children: ['detailsCanvasGrid'],
      itemId: 'dataStoreReference1',
      parent: 'flexItem6',
      props: {
        alias: 'details',
      },
      type: 'DataStoreReference',
    },
    dataStoreReference2: {
      children: ['boxList'],
      itemId: 'dataStoreReference2',
      parent: 'flexItem7',
      props: {
        alias: 'summary',
      },
      type: 'DataStoreReference',
    },
    dataStoreReference3: {
      children: ['flexPanel'],
      itemId: 'dataStoreReference3',
      parent: 'listTabItem',
      props: {
        alias,
      },
      type: 'DataStoreReference',
    },
    dataStoreReference4: {
      children: ['flexPanel1'],
      itemId: 'dataStoreReference4',
      parent: 'editTabItem',
      props: {
        alias,
      },
      type: 'DataStoreReference',
    },
    detailsCanvasGrid: {
      children: [],
      itemId: 'detailsCanvasGrid',
      parent: 'dataStoreReference1',
      props: {
        showBorder: false,
      },
      type: 'CanvasGrid',
    },
    editTabItem: {
      children: ['iconButton2', 'button1', 'dataStoreReference4'],
      itemId: 'editTabItem',
      parent: 'tabPanel',
      props: {
        icon: {
          iconName: 'edit',
          iconType: 'light',
        },
        label: {
          type: 'text',
          value: 'Edit',
        },
      },
      type: 'TabItem',
    },
    flexItem: {
      children: ['boxParent'],
      itemId: 'flexItem',
      parent: 'flexPanel',
      props: {
        backgroundColor: 'transparent',
        border: {
          bottom: {
            borderColor: { a: 0.12, b: 0, g: 0, r: 0 },
            style: 'solid',
            width: 1,
          },
          left: {
            style: 'solid',
            width: 0,
          },
          right: {
            style: 'solid',
            width: 0,
          },
          top: {
            style: 'solid',
            width: 0,
          },
        },
        padding: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0,
        },
        sizeType: 'auto',
      },
      type: 'FlexItem',
    },
    flexItem1: {
      children: ['canvasGrid'],
      itemId: 'flexItem1',
      parent: 'flexPanel',
      props: {
        backgroundColor: 'transparent',
        padding: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 16,
        },
      },
      type: 'FlexItem',
    },
    flexItem2: {
      children: ['form'],
      itemId: 'flexItem2',
      parent: 'flexPanel1',
      props: {},
      type: 'FlexItem',
    },
    flexItem3: {
      children: ['chartjs1'],
      itemId: 'flexItem3',
      parent: 'flexPanel2',
      props: {
        border: {
          bottom: {
            style: 'solid',
            width: 0,
          },
          left: {
            borderColor: { a: 0.07, b: 0, g: 0, r: 0 },
            style: 'solid',
            width: 16,
          },
          right: {
            style: 'solid',
            width: 0,
          },
          top: {
            style: 'solid',
            width: 0,
          },
        },
        padding: {
          all: 16,
        },
      },
      type: 'FlexItem',
    },
    flexItem4: {
      children: ['paper'],
      itemId: 'flexItem4',
      parent: 'flexPanel3',
      props: {
        backgroundColor: 'transparent',
        padding: {
          bottom: 0,
          left: 16,
          right: 16,
          top: 16,
        },
        sizeType: 'auto',
      },
      type: 'FlexItem',
    },
    flexItem5: {
      children: ['flexPanel2'],
      itemId: 'flexItem5',
      parent: 'flexPanel3',
      props: {
        backgroundColor: 'transparent',
        padding: {
          bottom: 0,
          left: 16,
          right: 16,
          top: 0,
        },
      },
      type: 'FlexItem',
    },
    flexItem6: {
      children: ['dataStoreReference1'],
      itemId: 'flexItem6',
      parent: 'flexPanel3',
      props: {
        backgroundColor: 'transparent',
        padding: {
          bottom: 16,
          left: 16,
          right: 16,
          top: 0,
        },
      },
      type: 'FlexItem',
    },
    flexItem7: {
      children: ['dataStoreReference2'],
      itemId: 'flexItem7',
      parent: 'flexPanel3',
      props: {
        backgroundColor: 'transparent',
        sizeType: 'auto',
      },
      type: 'FlexItem',
    },
    flexItem8: {
      children: ['chartjs'],
      itemId: 'flexItem8',
      parent: 'flexPanel2',
      props: {
        padding: {
          all: 16,
        },
      },
      type: 'FlexItem',
    },
    flexPanel: {
      children: ['flexItem', 'flexItem1'],
      itemId: 'flexPanel',
      parent: 'dataStoreReference3',
      props: {
        padding: {
          bottom: 16,
          left: 16,
          right: 16,
          top: 0,
        },
      },
      type: 'FlexPanel',
    },
    flexPanel1: {
      children: ['flexItem2'],
      itemId: 'flexPanel1',
      parent: 'dataStoreReference4',
      props: {
        padding: {
          all: 16,
        },
      },
      type: 'FlexPanel',
    },
    flexPanel2: {
      children: ['flexItem8', 'flexItem3'],
      itemId: 'flexPanel2',
      parent: 'flexItem5',
      props: {
        border: {
          all: {
            borderColor: { a: 0.07, b: 0, g: 0, r: 0 },
            style: 'solid',
            width: 16,
          },
        },
        renderAs: 'columns',
      },
      type: 'FlexPanel',
    },
    flexPanel3: {
      children: ['flexItem7', 'flexItem5', 'flexItem4', 'flexItem6'],
      itemId: 'flexPanel3',
      parent: 'dashboardTabItem',
      props: {},
      type: 'FlexPanel',
    },
    form: {
      children: [],
      itemId: 'form',
      parent: 'flexItem2',
      props: {
        formHorizontal: true,
        labelAlignment: 'right',
        labelWidth: 160,
      },
      type: 'Form',
    },
    icon: {
      children: ['triggerOnClick6'],
      itemId: 'icon',
      parent: 'actionIconsColumn',
      props: {
        icon: {
          iconName: 'edit',
          iconType: 'light',
        },
      },
      type: 'Icon',
    },
    icon1: {
      children: ['triggerOnClick5'],
      itemId: 'icon1',
      parent: 'actionIconsColumn',
      props: {
        color: { type: 'expression', value: '$.theme.palette.error.main' },
        icon: {
          iconName: 'trash',
          iconType: 'light',
        },
      },
      type: 'Icon',
    },
    iconButton: {
      children: ['triggerOnClick'],
      itemId: 'iconButton',
      parent: 'box1',
      props: {
        icon: {
          iconName: 'plus',
          iconType: 'light',
        },
        toolTip: {
          type: 'text',
          value: 'Add New',
        },
      },
      type: 'IconButton',
    },
    iconButton1: {
      children: ['triggerOnClick1'],
      itemId: 'iconButton1',
      parent: 'box1',
      props: {
        icon: {
          iconName: 'undo',
          iconType: 'light',
        },
        toolTip: {
          type: 'text',
          value: 'Reset',
        },
      },
      type: 'IconButton',
    },
    iconButton2: {
      children: ['triggerOnClick7'],
      itemId: 'iconButton2',
      parent: 'editTabItem',
      props: {
        icon: {
          iconName: 'undo',
          iconType: 'light',
        },
      },
      type: 'IconButton',
    },
    iconButton3: {
      children: ['triggerOnClick11'],
      itemId: 'iconButton3',
      parent: 'dashboardTabItem',
      props: {
        icon: {
          iconName: 'plus-circle',
          iconType: 'light',
        },
      },
      type: 'IconButton',
    },
    listTabItem: {
      children: ['dataStoreReference3'],
      itemId: 'listTabItem',
      parent: 'tabPanel',
      props: {
        icon: {
          iconName: 'list-alt',
          iconType: 'light',
        },
        label: {
          type: 'text',
          value: 'List',
        },
      },
      type: 'TabItem',
    },
    metricBox: {
      children: [
        'metricColorAndIcon1',
        'metricColorAndIcon',
        'metricColorAndIcon3',
        'metricColorAndIcon2',
        'triggerOnClick10',
      ],
      itemId: 'metricBox',
      parent: 'boxList',
      props: {
        colorAndIconSwitchCondition: {
          type: 'expression',
          value: '$.summary.createdBy',
        },
        title: {
          type: 'expression',
          value:
            "decode($.summary.createdBy, 'H', 'High', 'L', 'Low', 'C', 'Critical', 'M','Medium', $.summary.createdBy)",
        },
        trendDescription: {
          type: 'text',
          value: '20% compared to last month',
        },
        value: {
          type: 'expression',
          value: `\`\${$.summary.${idAttr.attributeCode}}\``,
        },
      },
      type: 'MetricBox',
    },
    metricColorAndIcon: {
      children: [],
      itemId: 'metricColorAndIcon',
      parent: 'metricBox',
      props: {
        caseValue: 'L',
        color: 'secondary',
        icon: {
          iconName: 'meh',
          iconType: 'light',
        },
      },
      type: 'MetricColorAndIcon',
    },
    metricColorAndIcon1: {
      children: [],
      itemId: 'metricColorAndIcon1',
      parent: 'metricBox',
      props: {
        caseValue: 'C',
        color: 'error',
        icon: {
          iconName: 'angry',
          iconType: 'light',
        },
      },
      type: 'MetricColorAndIcon',
    },
    metricColorAndIcon2: {
      children: [],
      itemId: 'metricColorAndIcon2',
      parent: 'metricBox',
      props: {
        icon: {
          iconName: 'analytics',
          iconType: 'light',
        },
      },
      type: 'MetricColorAndIcon',
    },
    metricColorAndIcon3: {
      children: [],
      itemId: 'metricColorAndIcon3',
      parent: 'metricBox',
      props: {
        caseValue: 'M',
        color: 'success',
        icon: {
          iconName: 'meh',
          iconType: 'light',
        },
      },
      type: 'MetricColorAndIcon',
    },
    paper: {
      children: ['dataStoreReference'],
      itemId: 'paper',
      parent: 'flexItem4',
      props: {
        padding: {
          all: 0,
        },
        variant: 'outlined',
      },
      type: 'Paper',
    },
    root: {
      children: ['folder', 'tabPanel'],
      itemId: 'root',
      parent: 'root',
      props: {
        minHeight: 760,
        padding: {
          all: 0,
        },
        windowTitle: {
          type: 'text',
          value: 'Dashboard',
        },
      },
      type: 'Page',
    },
    rowAction: {
      children: ['triggerOnClick3'],
      itemId: 'rowAction',
      parent: 'rowSelectionColumn',
      props: {
        icon: {
          iconName: 'trash',
          iconType: 'light',
        },
        iconColor: 'error',
        maxRowsSelected: 1,
        text: {
          type: 'text',
          value: 'Delete Row',
        },
      },
      type: 'RowAction',
    },
    rowAction1: {
      children: ['triggerOnClick4'],
      itemId: 'rowAction1',
      parent: 'rowSelectionColumn',
      props: {
        icon: {
          iconName: 'trash-alt',
          iconType: 'light',
        },
        iconColor: 'error',
        text: {
          type: 'text',
          value: 'Delete Selected Rows',
        },
      },
      type: 'RowAction',
    },
    rowNumberColumn: {
      children: [],
      itemId: 'rowNumberColumn',
      parent: 'canvasGrid',
      props: {
        label: { type: 'text', value: '#' },
        width: 41,
      },
      type: 'RowNumberColumn',
    },
    boxParent: {
      children: ['box', 'box1'],
      itemId: 'boxParent',
      parent: 'flexItem',
      props: {
        boxHeight: '1',
        gap: '8px',
      },
      type: 'Box',
    },
    rowSelectionColumn: {
      children: ['rowAction', 'rowAction1'],
      itemId: 'rowSelectionColumn',
      parent: 'canvasGrid',
      props: {},
      type: 'RowSelectionColumn',
    },
    smartSearch: {
      children: [],
      itemId: 'smartSearch',
      parent: 'box',
      props: {
        border: 'none',
        gridId: 'canvasGrid',
      },
      type: 'SmartSearch',
    },
    smartSearch1: {
      children: [],
      itemId: 'smartSearch1',
      parent: 'dataStoreReference',
      props: {
        border: 'bottom',
        gridId: 'detailsCanvasGrid',
      },
      type: 'SmartSearch',
    },
    tabPanel: {
      children: ['dashboardTabItem', 'listTabItem', 'editTabItem'],
      itemId: 'tabPanel',
      parent: 'root',
      props: {
        activeTab: 'dashboardTabItem',
        iconSize: 32,
        tabHeaderPadding: { all: 16 },
      },
      type: 'TabPanel',
    },
    triggerBeforeQuery: {
      children: [
        'actionSetGroupByAttribute',
        'actionSetAggregateAttribute',
        'actionSetQueryFilter',
      ],
      itemId: 'triggerBeforeQuery',
      parent: 'dataStore1',
      props: {},
      type: 'TriggerBeforeQuery',
    },
    triggerOnClick: {
      children: ['actionCreate', 'actionUpdateProp'],
      itemId: 'triggerOnClick',
      parent: 'iconButton',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick1: {
      children: ['actionReset'],
      itemId: 'triggerOnClick1',
      parent: 'iconButton1',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick10: {
      children: ['actionQuery1'],
      itemId: 'triggerOnClick10',
      parent: 'metricBox',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick11: {
      children: ['actionCreate1', 'actionUpdateProp2'],
      itemId: 'triggerOnClick11',
      parent: 'iconButton3',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick2: {
      children: ['actionSave'],
      itemId: 'triggerOnClick2',
      parent: 'button',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick3: {
      children: ['actionDeleteCurrentRecord'],
      itemId: 'triggerOnClick3',
      parent: 'rowAction',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick4: {
      children: ['actionDeleteSelectedRecords'],
      itemId: 'triggerOnClick4',
      parent: 'rowAction1',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick5: {
      children: ['actionDeleteCurrentRecord1'],
      itemId: 'triggerOnClick5',
      parent: 'icon1',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick6: {
      children: ['actionUpdateProp1'],
      itemId: 'triggerOnClick6',
      parent: 'icon',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick7: {
      children: ['actionReset1'],
      itemId: 'triggerOnClick7',
      parent: 'iconButton2',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick8: {
      children: ['actionSave1'],
      itemId: 'triggerOnClick8',
      parent: 'button1',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnClick9: {
      children: ['actionQuery'],
      itemId: 'triggerOnClick9',
      parent: 'chartjs',
      props: {},
      type: 'TriggerOnClick',
    },
    triggerOnRowClick: {
      children: ['actionUpdateProp3'],
      itemId: 'triggerOnRowClick',
      parent: 'canvasGrid',
      props: {},
      type: 'TriggerOnRowClick',
    },
  };
  return page;
}

const GridWithSearchAndEditFormInsideTabPanel: GeneratePage = async ({
  platform,
  store,
  appUid,
  pageId,
}: {
  platform: Platform;
  store?: Store<unknown> | null;
  appUid: string;
  pageId: string;
}): Promise<Page> => {
  const attrs = store ? await store.getAttributes() : [];
  let page = getPage(store ? store.ds : '', attrs);
  if (store) {
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'form',
      silent: true,
      appUid,
      pageId,
    });
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'smartSearch',
      silent: true,
      appUid,
      pageId,
    });
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'smartSearch1',
      silent: true,
      appUid,
      pageId,
    });
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'canvasGrid',
      silent: true,
      appUid,
      pageId,
    });
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId: 'detailsCanvasGrid',
      silent: true,
      appUid,
      pageId,
    });
  }
  return page;
};

export default GridWithSearchAndEditFormInsideTabPanel;
